import { Title } from '@solidjs/meta'
import { createSignal } from 'solid-js'
import Button from '~/components/Button'
import Card from '~/components/Card'
import ColorPicker from '~/components/ColorPicker'
import Container from '~/components/Container'
import Icon from '~/components/Icon'

const ColorPickerPage = () => {
  const [color1, setColor1] = createSignal('#3b82f6')
  const [color2, setColor2] = createSignal('rgb(239, 68, 68)')
  const [color3, setColor3] = createSignal('hsl(142, 71%, 45%)')
  const [color4, setColor4] = createSignal('#8b5cf6')
  const [formData, setFormData] = createSignal({
    primaryColor: '#3b82f6',
    secondaryColor: '#ec4899',
    backgroundColor: '#ffffff'
  })

  const handleFormSubmit = (e: Event) => {
    e.preventDefault()
    console.log('Form submitted:', formData())
  }

  return (
    <>
      <Title>ColorPicker - Florez Jaramillo Solutions</Title>

      <Container class="py-8">
        <div class="mb-10 mt-6">
          <h1 class="text-4xl font-bold text-foreground mb-4">ColorPicker</h1>
          <p class="text-foreground-subtle text-xl max-w-3xl leading-relaxed">
            Selector de color completo estilo Adobe con área de saturación, sliders de hue y alpha, inputs RGB, presets y múltiples formatos de salida.
          </p>
        </div>

        <div class="space-y-12">
          <Card>
            <Card.Header title="Básico" subtitle="ColorPicker con configuración por defecto." icon={<Icon icon="material-symbols-light:palette-outline" class="text-primary-500" size={24} />} />
            <Card.Body>
              <div class="flex items-center gap-6">
                <ColorPicker defaultValue="#3b82f6" onChange={setColor1} />
                <div class="flex-1 space-y-2">
                  <p class="text-sm font-medium text-foreground-subtle">Color seleccionado:</p>
                  <div class="flex items-center gap-3 rounded-lg border border-border bg-surface-muted p-3">
                    <div class="h-8 w-8 rounded-lg border-2 border-border" style={{ background: color1() }} />
                    <code class="font-mono text-sm text-foreground">{color1()}</code>
                  </div>
                </div>
              </div>
            </Card.Body>
          </Card>

          <Card>
            <Card.Header
              title="Diferentes formatos"
              subtitle="Usando valores iniciales en RGB, HSL y HEX."
              icon={<Icon icon="material-symbols-light:format-color-fill" class="text-secondary-500" size={24} />}
            />
            <Card.Body>
              <div class="grid grid-cols-1 gap-6 md:grid-cols-3">
                <div class="space-y-2">
                  <p class="text-sm font-medium text-foreground-subtle">RGB</p>
                  <ColorPicker defaultValue="rgb(239, 68, 68)" onChange={setColor2} />
                  <code class="block rounded-lg bg-surface-muted p-2 text-xs text-foreground">{color2()}</code>
                </div>
                <div class="space-y-2">
                  <p class="text-sm font-medium text-foreground-subtle">HSL</p>
                  <ColorPicker defaultValue="hsl(142, 71%, 45%)" onChange={setColor3} />
                  <code class="block rounded-lg bg-surface-muted p-2 text-xs text-foreground">{color3()}</code>
                </div>
                <div class="space-y-2">
                  <p class="text-sm font-medium text-foreground-subtle">HEX</p>
                  <ColorPicker defaultValue="#8b5cf6" onChange={setColor4} />
                  <code class="block rounded-lg bg-surface-muted p-2 text-xs text-foreground">{color4()}</code>
                </div>
              </div>
            </Card.Body>
          </Card>

          <Card>
            <Card.Header
              title="Con Form"
              subtitle="Integración con el componente Form para selección de colores en formularios."
              icon={<Icon icon="material-symbols-light:brush" class="text-success-500" size={24} />}
            />
            <Card.Body>
              <form onSubmit={handleFormSubmit} class="space-y-6">
                <div class="grid grid-cols-1 gap-6 md:grid-cols-3">
                  <div class="space-y-2">
                    <span class="block text-sm font-medium text-foreground">Color primario</span>
                    <ColorPicker value={formData().primaryColor} onChange={value => setFormData(prev => ({ ...prev, primaryColor: value }))} showAlpha={false} />
                  </div>
                  <div class="space-y-2">
                    <span class="block text-sm font-medium text-foreground">Color secundario</span>
                    <ColorPicker value={formData().secondaryColor} onChange={value => setFormData(prev => ({ ...prev, secondaryColor: value }))} showAlpha={false} />
                  </div>
                  <div class="space-y-2">
                    <span class="block text-sm font-medium text-foreground">Color de fondo</span>
                    <ColorPicker value={formData().backgroundColor} onChange={value => setFormData(prev => ({ ...prev, backgroundColor: value }))} showAlpha={true} />
                  </div>
                </div>

                <div class="flex items-center gap-4 rounded-lg border border-border bg-surface-muted p-4">
                  <span class="text-sm font-medium text-foreground-subtle">Vista previa:</span>
                  <div class="flex gap-2">
                    <div class="h-12 w-12 rounded-lg border-2 border-border" style={{ background: formData().primaryColor }} />
                    <div class="h-12 w-12 rounded-lg border-2 border-border" style={{ background: formData().secondaryColor }} />
                    <div class="h-12 w-12 rounded-lg border-2 border-border" style={{ background: formData().backgroundColor }} />
                  </div>
                </div>

                <div class="flex justify-end gap-3">
                  <Button variant="outlined" type="button">
                    Cancelar
                  </Button>
                  <Button variant="contained" type="submit">
                    Guardar
                  </Button>
                </div>
              </form>
            </Card.Body>
          </Card>

          {/* Tamaños */}
          <Card>
            <Card.Header
              title="Tamaños"
              subtitle="Cinco densidades alineadas con Select y Button: xs, sm, md, lg y xl."
              icon={<Icon icon="material-symbols-light:straighten" class="text-primary-500" size={24} />}
            />
            <Card.Body>
              <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 p-10 bg-surface-muted rounded-2xl border border-border">
                <ColorPicker size="xs" defaultValue="#ef4444" />
                <ColorPicker size="sm" defaultValue="#f97316" />
                <ColorPicker size="md" defaultValue="#3b82f6" />
                <ColorPicker size="lg" defaultValue="#22c55e" />
                <ColorPicker size="xl" defaultValue="#8b5cf6" />
              </div>
            </Card.Body>
          </Card>

          <Card>
            <Card.Header title="API Reference" subtitle="Propiedades del componente ColorPicker." icon={<Icon icon="material-symbols-light:code" class="text-foreground-subtle" size={24} />} />
            <Card.Body>
              <div class="overflow-x-auto">
                <table class="w-full text-left text-sm">
                  <thead class="border-b border-border text-foreground-subtle">
                    <tr>
                      <th class="px-4 py-3 font-medium">Propiedad</th>
                      <th class="px-4 py-3 font-medium">Tipo</th>
                      <th class="px-4 py-3 font-medium">Default</th>
                      <th class="px-4 py-3 font-medium">Descripción</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-border">
                    <tr>
                      <td class="px-4 py-3 font-mono text-primary">defaultValue</td>
                      <td class="px-4 py-3 text-foreground-subtle">string</td>
                      <td class="px-4 py-3 font-mono">'#3b82f6'</td>
                      <td class="px-4 py-3 text-foreground-muted">Color inicial (hex, rgb, hsl)</td>
                    </tr>
                    <tr>
                      <td class="px-4 py-3 font-mono text-primary">size</td>
                      <td class="px-4 py-3 text-foreground-subtle">'xs' | 'sm' | 'md' | 'lg' | 'xl'</td>
                      <td class="px-4 py-3 font-mono">'md'</td>
                      <td class="px-4 py-3 text-foreground-muted">Altura del disparador (36/40/48/56/64px)</td>
                    </tr>
                    <tr>
                      <td class="px-4 py-3 font-mono text-primary">value</td>
                      <td class="px-4 py-3 text-foreground-subtle">string</td>
                      <td class="px-4 py-3 font-mono">-</td>
                      <td class="px-4 py-3 text-foreground-muted">Color controlado</td>
                    </tr>
                    <tr>
                      <td class="px-4 py-3 font-mono text-primary">onChange</td>
                      <td class="px-4 py-3 text-foreground-subtle">{'(value: string) => void'}</td>
                      <td class="px-4 py-3 font-mono">-</td>
                      <td class="px-4 py-3 text-foreground-muted">Callback cuando cambia el color</td>
                    </tr>
                    <tr>
                      <td class="px-4 py-3 font-mono text-primary">outputFormat</td>
                      <td class="px-4 py-3 text-foreground-subtle">'hex' | 'rgb' | 'rgba' | 'hsl' | 'hsla'</td>
                      <td class="px-4 py-3 font-mono">'hex'</td>
                      <td class="px-4 py-3 text-foreground-muted">Formato de salida</td>
                    </tr>
                    <tr>
                      <td class="px-4 py-3 font-mono text-primary">showAlpha</td>
                      <td class="px-4 py-3 text-foreground-subtle">boolean</td>
                      <td class="px-4 py-3 font-mono">true</td>
                      <td class="px-4 py-3 text-foreground-muted">Muestra slider de transparencia</td>
                    </tr>
                    <tr>
                      <td class="px-4 py-3 font-mono text-primary">showPresets</td>
                      <td class="px-4 py-3 text-foreground-subtle">boolean</td>
                      <td class="px-4 py-3 font-mono">true</td>
                      <td class="px-4 py-3 text-foreground-muted">Muestra paleta de presets</td>
                    </tr>
                    <tr>
                      <td class="px-4 py-3 font-mono text-primary">showInputs</td>
                      <td class="px-4 py-3 text-foreground-subtle">boolean</td>
                      <td class="px-4 py-3 font-mono">true</td>
                      <td class="px-4 py-3 text-foreground-muted">Muestra inputs RGB numéricos</td>
                    </tr>
                    <tr>
                      <td class="px-4 py-3 font-mono text-primary">presets</td>
                      <td class="px-4 py-3 text-foreground-subtle">string[]</td>
                      <td class="px-4 py-3 font-mono">[...]</td>
                      <td class="px-4 py-3 text-foreground-muted">Paleta de colores predefinidos</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </Card.Body>
          </Card>
        </div>
      </Container>
    </>
  )
}

export default ColorPickerPage
